/**
 * LES PIÈCES D'IDENTITÉ ADMISES — un seul vocabulaire, lu de la base.
 *
 * ⚠️ J'AI INVENTÉ CE VOCABULAIRE AVANT DE LE LIRE, ET LA BASE M'A REFUSÉ.
 * Mesuré le 13/09/2026 en production : mon formulaire proposait
 * « carte_identite » et « attestation_identite » — ni l'un ni l'autre n'existe
 * dans `identifiant_personne_type_check`. L'enrôlement échouait sur une
 * contrainte, APRÈS que l'invitation ait été émise et le code remis à l'agent.
 *
 * ⚠️ ET IL MANQUAIT CINQ VALEURS RÉELLES : `nif`, `tin`, `carte_electeur`,
 * `rccm`, `mowibo_id`. La plus importante est `carte_electeur` : en Guinée
 * c'est elle qui sert de pièce d'identité courante, et mon menu ne la
 * proposait pas. Inventer un vocabulaire, c'est aussi effacer celui des gens.
 *
 * ⚠️⚠️ ET ELLE EXISTAIT EN QUATRE EXEMPLAIRES — mesuré le 14/09/2026, au
 * lendemain du correctif ci-dessus. `src/domaine/formulaires.ts` exportait une
 * constante du **MÊME NOM**, `PIECES_IDENTITE`, avec un contenu DIFFÉRENT ;
 * `src/partage/libelles.ts` suivait celle-là ; et `frontend/src/guichet.tsx`
 * portait une quatrième copie codée en dur, dont la valeur PAR DÉFAUT était
 * `carte_nationale` — que la base refuse. Créer la bonne liste sans retirer
 * l'ancienne n'avait donc rien corrigé pour les formulaires d'État : les TROIS
 * demandes déposées portent `pieceType: "carte_cedeao"`, valeur que
 * `fon.identifiant_personne` rejetait. La migration 096 a admis `carte_cedeao`
 * en base — c'est la carte qui PORTE le NIN — et les trois autres listes
 * ré-exportent celle-ci.
 *
 * ⚠️⚠️ ET J'AI FAILLI CONDAMNER LE PASSÉ EN LA RESSERRANT — 14/09/2026.
 * En retirant `carte_nationale` de cette liste, j'ai rendu INVALIDES trois
 * dossiers déjà déposés : GN-DEM-000000001 (aboutie), 000000002 et 000000003
 * (en traitement, déposée le jour même). Mesuré, pas supposé :
 * `jsonb_path_query(formulaire,'$.**.pieceType')` sur `fon.demande` rend
 * « carte_cedeao », « carte_nationale », « passeport ».
 *
 * ⚠️ CE QUI DÉPARTAGE UNE VALEUR RÉELLE D'UNE VALEUR INVENTÉE : LE DÉPÔT.
 * `carte_identite` et `attestation_identite` — refusées la veille — n'ont
 * jamais été déposées par personne. `carte_nationale` est portée par trois
 * dossiers d'État et s'imprime sur un acte de donation signé. La CNI guinéenne
 * existe ; c'était la contrainte qui ne la connaissait pas (migration 097).
 * Un vocabulaire se resserre vers l'avant, jamais vers l'arrière : un
 * formulaire qu'aucun agent ne peut plus revalider est un dossier bloqué, et
 * le citoyen n'y est pour rien.
 *
 * ⚠️ CETTE LISTE EST CONFRONTÉE À LA CONTRAINTE PAR UN ESSAI
 * (`tests/enrolement-agent.test.ts`). Une valeur ajoutée en base et pas ici
 * n'est jamais proposée ; une valeur ici et pas en base fait échouer
 * l'enrôlement au dernier moment. Les deux se voient au rouge, pas en
 * production.
 */
export const PIECES_IDENTITE = [
  'carte_electeur', 'carte_nationale', 'carte_cedeao', 'passeport', 'permis_conduire', 'acte_naissance',
  'nif', 'tin', 'rccm', 'mowibo_id',
] as const;

export type PieceIdentite = (typeof PIECES_IDENTITE)[number];

/** Ce que l'agent lit à l'écran. Le code reste la valeur de la base. */
export const LIBELLE_PIECE: Record<PieceIdentite, string> = {
  carte_electeur: "Carte d'électeur",
  carte_nationale: "Carte nationale d'identité",
  carte_cedeao: "Carte d'identité CEDEAO",
  passeport: 'Passeport',
  permis_conduire: 'Permis de conduire',
  acte_naissance: 'Acte de naissance',
  nif: "NIF — numéro d'identification fiscale",
  tin: 'TIN — identifiant fiscal international',
  rccm: 'RCCM — registre du commerce (personne morale)',
  mowibo_id: 'Mowibo ID',
};
